/**
 * EmployeeDetailForm — the ONE shared six-tab ALPACA Employee detail layout
 * (Basic Information · Organization · Contact · Resume · User Information ·
 * Roles), rendered identically by BOTH User & Security → Users (admin edit)
 * and My Details (self edit).
 *
 * Layout-only: each host page passes declarative tab/field descriptors built
 * from its OWN existing state + handlers, so all data logic stays in the page.
 * A field with an `onChange` renders a real editable input; a field without
 * one renders the greyed read-only value (booleans as the shared `.cellcheck`
 * span).  Required-yet-empty editable fields get the yellow tint + red *.
 */
import type { ReactNode } from 'react';
import { ComboSelect } from '../../components/ComboSelect';
import './usersDetail.css';

export interface EmpOption { value: string; label: string }

export interface EmpField {
  key: string;
  label: string;
  /** Default 'text'.  'select' uses the shared ComboSelect; 'radio' a radio
   *  row; 'checkbox' renders a real checkbox (editable) or `.cellcheck` (RO). */
  type?: 'text' | 'email' | 'password' | 'date' | 'textarea' | 'select' | 'radio' | 'checkbox';
  value: string | number | boolean | null;
  /** Omit to render the field READ-ONLY (greyed / cellcheck). */
  onChange?: (v: string | boolean) => void;
  required?: boolean;
  /** Options for select / radio (values compared as strings). */
  options?: EmpOption[];
  /** Span the full grid width (textarea defaults to full). */
  full?: boolean;
  placeholder?: string;
  maxLength?: number;
  hint?: string;
  /** Inline action button rendered next to the field (e.g. checkbox "Password"
   *  paired with a "Set Password" button — ALPACA's ActionContainerViewItem
   *  pattern, Model.xafml:22031). Shown regardless of readOnly (it opens a
   *  dialog; it doesn't edit the field's own value directly). */
  action?: { label: string; icon?: string; onClick: () => void };
}

export interface EmployeeTab {
  id: string;
  label: string;
  icon?: string;
  /** Declarative fields rendered in the standard compact grid. */
  fields?: EmpField[];
  /** Extra custom content under the fields (photo/signature, roles grid…). */
  content?: ReactNode;
  /** Field-grid columns (default 2). */
  cols?: 2 | 3;
}

function fieldStr(v: EmpField['value']): string {
  return v === null || v === undefined || typeof v === 'boolean' ? '' : String(v);
}

function Field({ f, readOnly }: { f: EmpField; readOnly?: boolean }) {
  // VIEW mode (ALPACA parity): the host page passes readOnly to force EVERY
  // field into the greyed read-only rendering until its Edit button is clicked.
  const editable = !!f.onChange && !readOnly;
  const type = f.type ?? 'text';
  const isEmpty = fieldStr(f.value).trim() === '';
  const inputCls = `ud-input${f.required && isEmpty ? ' required-empty' : ''}`;
  const label = (
    <label>
      {f.label}{f.required && <span className="ud-req"> *</span>}
    </label>
  );

  // Boolean — real checkbox when editable, shared cellcheck span when read-only.
  if (type === 'checkbox') {
    return (
      <div className="ud-field">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {editable ? (
            <label className="ud-check">
              <input type="checkbox" checked={!!f.value} onChange={e => f.onChange!(e.target.checked)} />
              {f.label}
            </label>
          ) : (
            <span className="ud-check" title="Read-only">
              <span className={`cellcheck${f.value ? ' on' : ''}`} role="img"
                aria-label={f.value ? 'Enabled' : 'Disabled'} /> {f.label}
            </span>
          )}
          {f.action && (
            <button type="button" className="ca-btn" onClick={f.action.onClick}>
              {f.action.icon && <i className={`ti ${f.action.icon}`} />} {f.action.label}
            </button>
          )}
        </div>
        {f.hint && <span className="ud-hint">{f.hint}</span>}
      </div>
    );
  }

  if (type === 'radio') {
    const cur = fieldStr(f.value);
    return (
      <div className="ud-field">
        {label}
        {editable ? (
          <div className="ud-radio-row">
            {(f.options ?? []).map(o => (
              <label key={o.value} className="ud-radio">
                <input type="radio" name={`empf-${f.key}`} checked={cur === o.value}
                  onChange={() => f.onChange!(o.value)} /> {o.label}
              </label>
            ))}
          </div>
        ) : (
          <div className={`ud-ro${cur === '' ? ' empty' : ''}`}>
            {(f.options ?? []).find(o => o.value === cur)?.label ?? (cur || '—')}
          </div>
        )}
      </div>
    );
  }

  if (type === 'select') {
    const cur = fieldStr(f.value);
    return (
      <div className="ud-field">
        {label}
        {editable ? (
          <ComboSelect value={cur} placeholder={f.placeholder ?? '—'}
            options={(f.options ?? []).map(o => ({ value: o.value, label: o.label }))}
            onChange={v => f.onChange!(v)} />
        ) : (
          <div className={`ud-ro${cur === '' ? ' empty' : ''}`}>
            {(f.options ?? []).find(o => o.value === cur)?.label ?? (cur || '—')}
          </div>
        )}
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <div className={`ud-field${f.full === false ? '' : ' full'}`}>
        {label}
        {editable ? (
          <textarea className={inputCls} rows={5} value={fieldStr(f.value)}
            maxLength={f.maxLength} placeholder={f.placeholder}
            onChange={e => f.onChange!(e.target.value)} />
        ) : (
          <div className={`ud-ro${isEmpty ? ' empty' : ''}`} style={{ whiteSpace: 'pre-wrap', minHeight: 90 }}>
            {isEmpty ? '—' : fieldStr(f.value)}
          </div>
        )}
        {f.hint && <span className="ud-hint">{f.hint}</span>}
      </div>
    );
  }

  // text / email / password / date
  return (
    <div className={`ud-field${f.full ? ' full' : ''}`}>
      {label}
      {editable ? (
        <input className={inputCls} type={type} value={fieldStr(f.value)}
          maxLength={f.maxLength} placeholder={f.placeholder}
          onChange={e => f.onChange!(e.target.value)} />
      ) : (
        <div className={`ud-ro${isEmpty ? ' empty' : ''}`}>{isEmpty ? '—' : fieldStr(f.value)}</div>
      )}
      {f.hint && <span className="ud-hint">{f.hint}</span>}
    </div>
  );
}

export function EmployeeDetailForm({ tabs, active, onTabChange, readOnly }: {
  tabs: EmployeeTab[];
  active: string;
  onTabChange: (id: string) => void;
  /** VIEW mode — render every field read-only regardless of its onChange. */
  readOnly?: boolean;
}) {
  const tab = tabs.find(t => t.id === active) ?? tabs[0];
  return (
    <>
      <div className="ud-tabs" role="tablist">
        {tabs.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={t.id === tab?.id}
            className={`ud-tab${t.id === tab?.id ? ' active' : ''}`}
            onClick={() => onTabChange(t.id)}>
            {t.icon && <i className={`ti ${t.icon}`} />} {t.label}
          </button>
        ))}
      </div>
      <div className="ud-tabbody">
        {tab?.fields && tab.fields.length > 0 && (
          <div className={`ud-grid cols-${tab.cols ?? 2}`}>
            {tab.fields.map(f => <Field key={f.key} f={f} readOnly={readOnly} />)}
          </div>
        )}
        {tab?.content && (
          <div style={tab.fields && tab.fields.length > 0 ? { marginTop: 18 } : undefined}>
            {tab.content}
          </div>
        )}
      </div>
    </>
  );
}
